import {Component, OnInit} from '@angular/core';
import {storageKey} from "../../api/enum/storage.constants";
import {InstitutesService} from "../../api/services/institutes.service";
import {ApiResponse} from "../../api/api-response.model";
import {AdmissionEnumData, BaseEnumData, EnumData, InstituteEnumData} from "../../api/enum/enum.model";
import {City, States} from "../../api/enum/city-state.model";
import {Router} from "@angular/router";
import {SessionStorageService} from "../../core/services/session-storage.service";
import {Institute} from "../../api/models/institute.model";
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Course, Program} from "../../api/models/program.model";
import {AdmissionsService} from "../../api/services/admissions.service";
import {AdmissionHeaderComponent} from "./components/admission-header/admission-header.component";
import {ProgramCourseSectionComponent} from "./components/program-course-section/program-course-section.component";
import {StudentInformationSectionComponent} from "./components/student-information-section/student-information-section.component";
import {GuardianSectionComponent} from "./components/guardian-section/guardian-section.component";
import {AddressSectionComponent} from "./components/address-section/address-section.component";
import {SiblingsSectionComponent} from "./components/siblings-section/siblings-section.component";

@Component({
    selector: 'app-admission',
    imports: [
        ReactiveFormsModule,
        AdmissionHeaderComponent,
        ProgramCourseSectionComponent,
        StudentInformationSectionComponent,
        GuardianSectionComponent,
        AddressSectionComponent,
        SiblingsSectionComponent
    ],
    templateUrl: './admission.component.html',
    styleUrl: './admission.component.scss'
})
export class AdmissionComponent implements OnInit {
    institute: Institute | null = null;
    programs: Program[] = [];
    courses: Course[] = [];
    allCourses: Course[] = [];

    // Enum model
    genders: EnumData[] = [];
    religions: EnumData[] = [];
    relations: EnumData[] = [];
    addressType: EnumData[] = [];
    motherTongue: EnumData[] = [];
    bloodGroupsType: EnumData[] = [];
    castCategoryType: EnumData[] = [];
    educationBoardType: EnumData[] = [];
    siblingRelationType: EnumData[] = [];

    defaultLogo: string = 'logo-login.svg';
    states: States[] = [];
    cities: City[] = [];

    admissionFeeAmount = null

    admissionForm: FormGroup;
    loading: boolean = false;

    constructor(
        private fb: FormBuilder,
        private storage: SessionStorageService,
        private instituteService: InstitutesService,
        private admissionsService: AdmissionsService,
        private router: Router
    ) {
        this.admissionForm = this.fb.group({
            program_course_details: this.fb.group({
                program_id: ['', Validators.required],
                course_id: new FormControl({value: '', disabled: true}, Validators.required),
            }),
            student_information: this.fb.group({
                first_name: ['', Validators.required],
                middle_name: [''],
                last_name: ['', Validators.required],
                gender_id: ['', Validators.required],
                date_of_birth: ['', Validators.required],
                place_of_birth: ['', Validators.required],
                religion_id: ['', Validators.required],
                mother_tongue_id: ['', Validators.required],
                blood_group_id: ['', Validators.required],
                category_id: ['', Validators.required],
                aadhaar: ['', [Validators.required, Validators.pattern('^[0-9]{12}$')]]
            }),
            // education_histories: this.fb.group({
            //     institution_name: ['', Validators.required],
            //     degree: ['', Validators.required],
            //     education_board: ['', Validators.required],
            //     transfer_certificate_no: ['', Validators.required],
            //     reason_for_leaving: ['', Validators.required],
            //     start_year: ['', Validators.required],
            //     end_year: ['', Validators.required]
            // }),
            guardian_information: this.fb.array([]),
            siblings: this.fb.array([]),
            address_details: this.fb.group({
                address_type_id: ['', Validators.required],
                address_line_1: ['', Validators.required],
                address_line_2: [''],
                city_id: new FormControl({value: '', disabled: true}, Validators.required),
                state_id: ['', Validators.required],
                pin_code: ['', Validators.required]
            }),
            additional_notes: ['']
        });

        // // Seed initial data if needed
        this.initialGuardian();
    }

    // Getters
    get guardians() {
        return this.admissionForm.get('guardian_information') as FormArray;
    }

    get siblings() {
        return this.admissionForm.get('siblings') as FormArray;
    }

    get programCourseGroup(): FormGroup {
        return this.admissionForm.get('program_course_details') as FormGroup;
    }

    get studentInfoGroup(): FormGroup {
        return this.admissionForm.get('student_information') as FormGroup;
    }

    get addressGroup(): FormGroup {
        return this.admissionForm.get('address_details') as FormGroup;
    }

    ngOnInit(): void {
        this.loadBaseEnumData();
        this.loadAdmissionEnumData();
        this.loadInstituteEnumData();
        this.loadCityStateEnumData();

        const instituteToken = this.storage.get(storageKey.instituteToken)

        this.instituteService.setInstituteToken = <string>instituteToken
        this.instituteService.getInstituteDetail().subscribe(response => {
            if (response.data) {
                this.institute = response.data;
            }
        })
        this.instituteService.getInstitutePrograms().subscribe(response => {
            if (response.data) {
                this.programs = response.data.programs
            }
        })

        // Listen for program change
        this.admissionForm.get('program_course_details.program_id')?.valueChanges.subscribe(programId => {
            const selectedProgram = this.programs.find(p => p.id === +programId);
            this.courses = selectedProgram ? selectedProgram.courses : [];
            this.admissionForm.get('program_course_details.course_id')?.reset();
            this.admissionForm.get('program_course_details.course_id')?.enable();

            // Map all course
            this.allCourses = this.programs.flatMap(p => p.courses);
        });


        this.admissionForm.get('address_details.state_id')?.valueChanges.subscribe(stateId => {
            this.onStateChange(stateId);
            this.admissionForm.get('address_details.city_id')?.enable();
        });

        this.admissionForm.get('program_course_details.course_id')?.valueChanges.subscribe(courseId => {
            if (courseId) {
                this.getApplicationFee(courseId);
            }
        });

    }


    addGuardian() {
        const g = this.fb.group({
            relation_id: ['', Validators.required,],
            first_name: ['', Validators.required],
            middle_name: [''],
            last_name: ['', Validators.required],
            mobile: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
            email: ['', [Validators.required, Validators.email]],
            qualification: [''],
            occupation: ['']
        });
        this.guardians.push(g);
    }

    initialGuardian() {
        const father = this.fb.group({
            relation_id: [1, Validators.required],
            first_name: ['', Validators.required],
            middle_name: [''],
            last_name: ['', Validators.required],
            mobile: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
            email: ['', [Validators.required, Validators.email]],
            qualification: [''],
            occupation: ['']
        });
        const mother = this.fb.group({
            relation_id: [2, Validators.required],
            first_name: ['', Validators.required],
            middle_name: [''],
            last_name: ['', Validators.required],
            mobile: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
            email: ['', [Validators.required, Validators.email]],
            qualification: [''],
            occupation: ['']
        });
        this.guardians.push(father);
        // this.guardians.push(mother);
    }

    removeGuardian(index: number) {
        if (this.guardians.length > 1) {
            this.guardians.removeAt(index);
        }
    }

    // SIBLING METHODS (ALL 6 FIELDS INCLUDED)
    addSibling(data: any = null) {
        this.siblings.push(this.fb.group({
            sin_number: [data?.sin_number || ''],
            first_name: [data?.first_name || '', Validators.required],
            middle_name: [data?.middle_name || ''],
            last_name: [data?.last_name || '', Validators.required],
            relation_id: [data?.relation_id || ''],
            course_id: [data?.course_id || '']
        }));
    }

    removeSibling(index: number) {
        this.siblings.removeAt(index);
    }

    onSubmit() {
        this.loading = true;
        const payload = this.admissionForm.value

        const apiPayload = this.arrangePayloadData(payload);

        const instituteToken = this.storage.get(storageKey.instituteToken)

        let _is_paid = this.admissionFeeAmount == 0 || this.admissionFeeAmount == null

        this.admissionsService.setInstituteToken = <string>instituteToken
        this.admissionsService.submitAdmission(apiPayload).subscribe({
            next: (res) => {
                if (!res.error) {
                    this.router.navigate(['/thank-you'], {
                        queryParams: { is_paid: _is_paid }
                    });
                } else {
                    this.loading = false;
                    console.error('Submission failed', res.message.message);
                }
            },
            error: (err) => {
                this.loading = false;

                if (err.status === 422) {
                    console.error('Submission failed', err.error.message.message);
                }

                console.error('Submission failed', err);
            }
        });
    }

    onStateChange(stateId: number): void {
        const selectedState = this.states.find(state => state.id === +stateId);

        this.cities = selectedState ? selectedState.cities : [];

        // Reset city dropdown
        this.admissionForm.patchValue({city_id: null});
    }

    getApplicationFee(courseId: number) {
        const instituteToken = this.storage.get(storageKey.instituteToken)
        this.admissionsService.setInstituteToken = <string>instituteToken
        this.admissionsService.getAdmissionApplicationFee(courseId).subscribe({
            next: (response) => {
                if (!response.error) {
                    console.log('get admission fee');
                    this.admissionFeeAmount = response.data.application_fee
                } else {
                    console.error('get admission fee failed', response.message.message);
                }
            },
            error: (err) => {
                console.error('get admission fee failed', err);
            }
        });
    }

    private loadBaseEnumData(): void {
        const response =
            this.storage.get<ApiResponse<BaseEnumData>>(
                storageKey.enumData
            );
        if (!response || response.error) {
            console.error('Enum data missing or invalid');
            this.router.navigateByUrl('/');
            return;
        }
        this.genders = response.data.gender_type;
        this.religions = response.data.religion_type;
    }

    private loadInstituteEnumData(): void {
        const response =
            this.storage.get<ApiResponse<InstituteEnumData>>(
                storageKey.instituteEnumData
            );
        if (!response || response.error) {
            console.error('Enum data missing or invalid');
            this.router.navigateByUrl('/');
            return;
        }
        this.addressType = response.data.address_type;
    }

    private loadAdmissionEnumData(): void {
        const response =
            this.storage.get<ApiResponse<AdmissionEnumData>>(
                storageKey.admissionEnumData
            );
        if (!response || response.error) {
            console.error('Enum data missing or invalid');
            this.router.navigateByUrl('/');
            return;
        }
        this.religions = response.data.religion_type;
        this.relations = response.data.relation_type;
        this.motherTongue = response.data.language_type;
        this.bloodGroupsType = response.data.blood_groups_type;
        this.castCategoryType = response.data.caste_category_type;
        this.educationBoardType = response.data.education_board_type;
        this.siblingRelationType = response.data.sibling_relation_type;
    }


    private loadCityStateEnumData(): void {
        const cityState =
            this.storage.get<ApiResponse<States[]>>(
                storageKey.cityState
            );

        if (!cityState || cityState.error) {
            console.error('Enum data missing or invalid');
            this.router.navigateByUrl('/');
            return;
        }
        this.states = cityState.data;
    }

    private arrangePayloadData(formData: any): any {
        return {
            program_course_details: {
                program_id: parseInt(formData.program_course_details.program_id),
                course_id: parseInt(formData.program_course_details.course_id)
            },
            student_information: {
                first_name: formData.student_information.first_name,
                middle_name: formData.student_information.middle_name,
                last_name: formData.student_information.last_name,
                gender_id: parseInt(formData.student_information.gender_id),
                date_of_birth: formData.student_information.date_of_birth,
                place_of_birth: formData.student_information.place_of_birth,
                religion_id: parseInt(formData.student_information.religion_id),
                mother_tongue_id: parseInt(formData.student_information.mother_tongue_id),
                blood_group_id: parseInt(formData.student_information.blood_group_id),
                category_id: parseInt(formData.student_information.category_id),
                aadhaar: formData.student_information.aadhaar
            },
            address_details: [
                {
                    address_type_id: parseInt(formData.address_details.address_type_id),
                    address_line_1: formData.address_details.address_line_1,
                    address_line_2: formData.address_details.address_line_2,
                    state_id: parseInt(formData.address_details.state_id),
                    city_id: parseInt(formData.address_details.city_id),
                    pin_code: formData.address_details.pin_code,
                }
            ],
            education_histories: [],
            guardian_information: formData.guardian_information.map((g: any) => ({
                ...g,
                relation_id: parseInt(g.relation_id)
            })),
            siblings: formData.siblings.map((s: any) => ({
                ...s,
                sin_number: parseInt(s.sin_number),
                course_id: parseInt(s.course_id),
                relation_id: parseInt(s.relation_id)
            })),
            additional_notes: formData.additional_notes
        };
    }

    allowNumbersOnly(event: any) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/[^0-9]/g, '');
    }

}
